import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Badge } from '@ds/components/core/Badge';
import { Button } from '@ds/components/core/Button';
import { Card, CardBody, CardHeader } from '@ds/components/core/Card';
import { AuthenticatedShell, Page } from '@/components/AppShell';
import { EmptyState } from '@/components/EmptyState';
import { Money } from '@/components/Money';
import { useInstitutions } from '@/features/institutions/api';
import { useCardInvoiceDetail, useCardInvoices } from '@/features/installments/api';
import { PayInvoiceDialog } from '@/features/installments/components/PayInvoiceDialog';
import { formatDate } from '@/lib/format';

export const Route = createFileRoute('/_authenticated/cartoes/$cardId')({ component: CardInvoicesPage });

const STATUS_LABEL: Record<string, string> = { aberta: 'Aberta', fechada: 'Fechada', paga: 'Paga' };

function CardInvoicesPage() {
  const { cardId } = Route.useParams();
  const { data: institutionsData } = useInstitutions(false);
  const { data: invoices, isLoading } = useCardInvoices(cardId);
  const [selectedClosingDate, setSelectedClosingDate] = useState<string | undefined>(undefined);
  const [payDialog, setPayDialog] = useState<{ open: boolean; closingDate?: string; totalCents?: number }>({ open: false });

  const card = useMemo(() => institutionsData?.flatMap((i) => i.cards ?? []).find((c) => c.id === cardId), [institutionsData, cardId]);
  const accounts = useMemo(() => institutionsData?.flatMap((i) => i.accounts ?? []) ?? [], [institutionsData]);
  const { data: detail } = useCardInvoiceDetail(cardId, selectedClosingDate);

  return (
    <AuthenticatedShell title={card ? `Fatura — ${card.nickname}` : 'Fatura'}>
      <Page>
        <div data-testid="card-invoices-page" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gutter-section)' }}>
          <Card>
            <CardHeader title="Períodos" />
            <CardBody>
              {isLoading ? (
                <p style={{ font: 'var(--type-body)', color: 'var(--text-muted)', margin: 0 }}>Carregando...</p>
              ) : !invoices || invoices.length === 0 ? (
                <EmptyState icon="receipt" title="Nenhuma fatura ainda" description="Compras parceladas no cartão aparecem aqui." testId="card-invoices-empty" />
              ) : (
                <div>
                  {invoices
                    .slice()
                    .reverse()
                    .map((invoice) => (
                      <div
                        key={invoice.closingDate}
                        data-testid={`invoice-row-${invoice.closingDate}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 4px', borderTop: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                        onClick={() => setSelectedClosingDate(invoice.closingDate)}
                      >
                        <div style={{ flex: 1 }}>
                          <span style={{ font: 'var(--type-label)', color: 'var(--text-primary)' }}>Fecha em {formatDate(invoice.closingDate)}</span>{' '}
                          <Badge tone={invoice.status === 'paga' ? 'positive' : invoice.status === 'fechada' ? 'warning' : 'neutral'}>{STATUS_LABEL[invoice.status]}</Badge>
                        </div>
                        <Money cents={invoice.totalCents} />
                        {invoice.status === 'fechada' ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            data-testid={`invoice-pay-${invoice.closingDate}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPayDialog({ open: true, closingDate: invoice.closingDate, totalCents: invoice.totalCents });
                            }}
                          >
                            Pagar
                          </Button>
                        ) : null}
                      </div>
                    ))}
                </div>
              )}
            </CardBody>
          </Card>

          {selectedClosingDate && detail ? (
            <Card>
              <CardHeader title={`Itens — fecha em ${formatDate(selectedClosingDate)}`} />
              <CardBody>
                <div data-testid="invoice-detail">
                  {detail.items.map((item) => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 4px', borderTop: '1px solid var(--border-subtle)' }}>
                      <div style={{ flex: 1 }}>
                        {item.description || 'Compra parcelada'} — {item.installmentNumber}/{item.installmentsTotal}
                      </div>
                      <Money cents={item.amountCents} />
                    </div>
                  ))}
                  {detail.refunds.map((refund) => (
                    <div
                      key={refund.id}
                      data-testid={`invoice-refund-${refund.id}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 4px', borderTop: '1px solid var(--border-subtle)' }}
                    >
                      <div style={{ flex: 1 }}>
                        {refund.description || 'Estorno'} <Badge tone="positive" outline>estorno</Badge>
                      </div>
                      <Money cents={refund.amountCents} tone="in" />
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          ) : null}
        </div>

        {payDialog.open && payDialog.closingDate !== undefined && payDialog.totalCents !== undefined ? (
          <PayInvoiceDialog
            open={payDialog.open}
            onClose={() => setPayDialog({ open: false })}
            cardId={cardId}
            closingDate={payDialog.closingDate}
            totalCents={payDialog.totalCents}
            accounts={accounts}
          />
        ) : null}
      </Page>
    </AuthenticatedShell>
  );
}
